import { Size } from '~/types/ThemeTypes'
import {
  createMultiStyleConfigHelpers,
  MultiStyleType,
  StyleThemingProps,
} from '~/utils/chakraUtils'
import { mergeMultiStyleConfig } from '~/utils/styleHelpers'

import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'

// import { callsComponentColorKeys } from './_colors'

// #region CallsListItemStyle
export const OPEN_ROW_BUTTON_CLASS = 'call-row-open-button'

const callsListItemStyleParts = ['container', 'rowWrapper'] as const
const helpers = createMultiStyleConfigHelpers(callsListItemStyleParts)

export const CallsListItemStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'flex',
      height: '4.75rem',
      backgroundColor: 'bg.subtle',
      px: '1rem',
      marginTop: '-2px',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '0.625rem',
      alignSelf: 'stretch',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      border: '1px solid',
      borderColor: 'transparent',
      _hover: {
        borderColor: 'border.muted',
        bg: 'bg.muted',
        '& > div': {
          borderBottomColor: 'transparent',
        },
      },
      _selected: {
        borderColor: 'border.muted',
        bg: 'bg.emphasized',
      },
      '&[data-first]': {
        marginTop: 0,
      },
      [`&:has(.${OPEN_ROW_BUTTON_CLASS}:focus-visible)`]: {
        outlineWidth: '2px',
        outlineStyle: 'solid',
        outlineColor: 'brand.fg',
        outlineOffset: '-2px',
      },
    },
    rowWrapper: {
      display: 'flex',
      alignItems: 'center',
      flex: '1 0 0',
      alignSelf: 'stretch',
      borderBottom: '1px solid',
      borderBottomColor: 'border.default',
      _hover: {
        borderBottomColor: 'transparent',
      },
    },
  },
  sizes: {
    [Size.sm]: {
      container: {
        px: '0.75rem',
      },
    },
  },
})

export const useCallsListItemStyles = (props?: StyleThemingProps) =>
  mergeMultiStyleConfig(CallsListItemStyle, props) as MultiStyleType<
    typeof callsListItemStyleParts
  >
// #endregion CallsListItemStyle

// #region DateTimeCellStyle
const dateTimeCellStyleParts = [
  'container',
  'timeWrapper',
  'timeText',
  'dateText',
] as const
const dateTimeHelpers = createMultiStyleConfigHelpers(dateTimeCellStyleParts)

export const DateTimeCellStyle = dateTimeHelpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'flex',
      pr: '1rem',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      flex: '1 0 0',
    },
    timeWrapper: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
    },
    timeText: {
      ...textStyles[textStyleKeys.Body1],
      color: 'fg.default',
    },
    dateText: {
      ...textStyles[textStyleKeys.Body3],
      color: 'fg.muted',
    },
  },
})

const dateTimeThemeKey = DateTimeCellStyle
export const useDateTimeCellStyles = (props?: StyleThemingProps) =>
  mergeMultiStyleConfig(dateTimeThemeKey, props) as MultiStyleType<
    typeof dateTimeCellStyleParts
  >
// #endregion DateTimeCellStyle

// #region OutcomeCellStyle
export enum CallOutcomeCellVariant {
  InProgress = 'inProgress',
  Success = 'green.solid',
  Failure = 'failure',
}

const outcomeCellStyleParts = [
  'container',
  'iconWrapper',
  'icon',
  'text',
] as const
const outcomeHelpers = createMultiStyleConfigHelpers(outcomeCellStyleParts)

export const OutcomeCellStyle = outcomeHelpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'flex',
      height: '3.75rem',
      alignItems: 'center',
      alignContent: 'center',
      gap: '0.5rem',
      flex: '1 0 0',
      flexWrap: 'wrap',
    },
    iconWrapper: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    icon: {
      boxSize: '16px',
      color: 'fg.default',
    },
    text: {
      ...textStyles[textStyleKeys.Body1],
    },
  },
  variants: {
    [CallOutcomeCellVariant.InProgress]: {
      text: {
        color: 'brand.solid',
      },
      icon: {
        color: 'brand.solid',
      },
    },
    [CallOutcomeCellVariant.Success]: {
      text: {
        color: 'fg.default',
      },
      icon: {
        color: 'fg.default',
      },
    },
    [CallOutcomeCellVariant.Failure]: {
      text: {
        color: 'red.solid',
      },
      icon: {
        color: 'red.solid',
      },
    },
  },
  defaultProps: {
    variant: CallOutcomeCellVariant.Success,
  },
})

const outcomeThemeKey = OutcomeCellStyle
export const useCallOutcomeCellStyles = (props?: StyleThemingProps) =>
  mergeMultiStyleConfig(outcomeThemeKey, props) as MultiStyleType<
    typeof outcomeCellStyleParts
  >
// #endregion OutcomeCellStyle

// #region VoicemailCellStyle
const voicemailCellStyleParts = ['container', 'icon'] as const
const voicemailHelpers = createMultiStyleConfigHelpers(voicemailCellStyleParts)

export const VoicemailCellStyle = voicemailHelpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'flex',
      height: '3.75rem',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      flex: '1 0 0',
    },
    icon: {
      boxSize: '16px',
      color: 'brand.fg',
    },
  },
})

const voicemailThemeKey = VoicemailCellStyle
export const useVoicemailCellStyles = (props?: StyleThemingProps) =>
  mergeMultiStyleConfig(voicemailThemeKey, props) as MultiStyleType<
    typeof voicemailCellStyleParts
  >
// #endregion VoicemailCellStyle

// #region ContactCellStyle
export const ContactCellStyleValues = {
  menuWidth: '13.75rem',
}

const contactCellStyleParts = [
  'container',
  'titleContainer',
  'subtitleContainer',
  'titleIconContainer',
  'titleIcon',
  'titleText',
  'titlePrefix',
  'subtitleText',
] as const

const contactHelpers = createMultiStyleConfigHelpers(contactCellStyleParts)

export const ContactCellStyle = contactHelpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'flex',
      height: '3.75rem',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      flex: '1 0 0',
    },
    titleContainer: {
      display: 'flex',
      height: '1.75rem',
      alignItems: 'center',
      gap: '0.5rem',
      pr: '1rem',
      flexShrink: 0,
      alignSelf: 'stretch',
    },
    subtitleContainer: {
      display: 'flex',
      alignItems: 'center',
      minHeight: '1.5rem',
      gap: '0.5rem',
      flexShrink: 0,
      alignSelf: 'stretch',
      pr: '1rem',
    },
    titleIconContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      boxSize: '1rem',
      borderRadius: '6.25rem',
    },
    titleIcon: {
      boxSize: '16px',
      color: 'fg.default',
    },
    titleText: {
      ...textStyles[textStyleKeys.Body1],
      color: 'fg.default',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    subtitleText: {
      ...textStyles[textStyleKeys.Body3],
      color: 'fg.muted',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    titlePrefix: {
      ...textStyles[textStyleKeys.Body3],
      color: 'fg.default',
    },
  },
})

const contactThemeKey = ContactCellStyle
export const useContactCellStyles = (props?: StyleThemingProps) =>
  mergeMultiStyleConfig(contactThemeKey, props) as MultiStyleType<
    typeof contactCellStyleParts
  >
// #endregion ContactCellStyle

// #region DurationCellStyle
const durationCellStyleParts = [
  'container',
  'wrapper',
  'text',
  'iconWrapper',
] as const
const durationHelpers = createMultiStyleConfigHelpers(durationCellStyleParts)

export const DurationCellStyle = durationHelpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'flex',
      height: '3.75rem',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      flex: '1 0 0',
    },
    wrapper: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-star',
      justifyContent: 'space-between',
    },
    iconWrapper: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
    },
    text: {
      ...textStyles[textStyleKeys.Body1],
      color: 'fg.default',
      minWidth: '3.3rem',
      textAlign: 'left',
    },
  },
})

const durationThemeKey = DurationCellStyle
export const useDurationCellStyles = (props?: StyleThemingProps) =>
  mergeMultiStyleConfig(durationThemeKey, props) as MultiStyleType<
    typeof durationCellStyleParts
  >
// #endregion DurationCellStyle

// #region TranscriptCellStyle
const transcriptCellStyleParts = [
  'container',
  'wrapper',
  'text',
  'iconWrapper',
] as const
const transcriptHelpers = createMultiStyleConfigHelpers(
  transcriptCellStyleParts,
)

export const TranscriptCellStyle = transcriptHelpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'flex',
      height: '3.75rem',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      flex: '1 0 0',
    },
    wrapper: {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
      justifyContent: 'space-between',
    },
    iconWrapper: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
    },
    text: {
      ...textStyles[textStyleKeys.Body1],
      color: 'fg.default',
      minWidth: '3.3rem',
      textAlign: 'left',
    },
  },
})

const transcriptThemeKey = TranscriptCellStyle
export const useTranscriptCellStyles = (props?: StyleThemingProps) =>
  mergeMultiStyleConfig(transcriptThemeKey, props) as MultiStyleType<
    typeof transcriptCellStyleParts
  >
// #endregion TranscriptCellStyle

export const CallsListItemCellStyles = {
  DateTimeCellStyle,
  OutcomeCellStyle,
  VoicemailCellStyle,
  ContactCellStyle,
  DurationCellStyle,
  TranscriptCellStyle,
}
